import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HardHat, LogOut } from 'lucide-react';
import { toast } from 'sonner';

import { useTranslation } from '@/hooks/useTranslation';
import { crewCheckout, getActiveCrews, type ActiveCrew } from '@/services/api.client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/**
 * Guard "crews on site" section — lists contractor crews with workers still
 * recorded inside today and lets the guard record how many are leaving in a
 * batch. Repeated partial check-outs are allowed until the inside count hits 0.
 */
export function CrewOnSiteSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const crewsQuery = useQuery({
    queryKey: ['guard', 'active-crews'],
    queryFn: getActiveCrews,
    refetchInterval: 30_000,
  });

  const crews = crewsQuery.data ?? [];

  if (crews.length === 0) return null;

  return (
    <section className="space-y-3" aria-label={t('crew.title')}>
      <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--text-primary)]">
        <HardHat className="size-4" aria-hidden="true" />
        {t('crew.title')}
      </h2>
      <ul className="space-y-2">
        {crews.map((crew) => (
          <CrewRow
            key={crew.id}
            crew={crew}
            onChanged={() =>
              void queryClient.invalidateQueries({ queryKey: ['guard', 'active-crews'] })
            }
          />
        ))}
      </ul>
    </section>
  );
}

function CrewRow({ crew, onChanged }: { crew: ActiveCrew; onChanged: () => void }) {
  const { t } = useTranslation();
  const [leaving, setLeaving] = useState<number>(crew.insideCount);

  const checkoutMutation = useMutation({
    mutationFn: (headcountOut: number) => crewCheckout(crew.id, headcountOut),
    onSuccess: (res) => {
      toast.success(t('crew.checkout.success'));
      if (!res.reconciled) setLeaving(res.insideCount);
      onChanged();
    },
    onError: () => toast.error(t('crew.checkout.error')),
  });

  return (
    <li className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-medium text-[var(--text-primary)]">{crew.contractorName}</p>
          <p className="text-xs text-[var(--text-secondary)]">
            {t('crew.unit')}: {crew.unitReference} · {crew.insideCount}{' '}
            {t('crew.inside').toLowerCase()}
          </p>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <label htmlFor={`crew-out-${crew.id}`} className="text-xs text-[var(--text-secondary)]">
          {t('crew.checkout.label')}
        </label>
        <Input
          id={`crew-out-${crew.id}`}
          type="number"
          min={1}
          max={crew.insideCount}
          value={leaving}
          onChange={(e) =>
            setLeaving(Math.min(crew.insideCount, Math.max(1, parseInt(e.target.value, 10) || 1)))
          }
          className="w-16 min-h-[44px] text-center"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={checkoutMutation.isPending || leaving < 1 || leaving > crew.insideCount}
          onClick={() => checkoutMutation.mutate(leaving)}
          className="min-h-[44px]"
        >
          <LogOut className="size-4 mr-1" aria-hidden="true" />
          {t('crew.checkout.confirm')}
        </Button>
      </div>
    </li>
  );
}
